import { getAuth } from 'firebase/auth';

export type TelemetryEvent = {
    provider: 'MASSIVE' | 'FMP' | 'APP';
    category: 'QUOTE' | 'AGGS' | 'DIVIDENDS' | 'ASSET_DETAIL' | 'CACHE' | 'UI';
    level: 'info' | 'warn' | 'error';
    symbol?: string;
    requestPath?: string;
    status?: number;
    message: string;
    meta?: Record<string, any>;
    createdAt?: any;
    expiresAt?: any;
    sessionId?: string;
    appVersion?: string;
};

const TTL_DAYS = 7;

const loadFirestoreDeps = async () => {
    const [firestore, firebase] = await Promise.all([import('firebase/firestore'), import('../firebase')]);
    return { firestore, db: firebase.db };
};

function getAppVersion() {
    if (typeof globalThis !== 'undefined' && (globalThis as any).__VITE_APP_VERSION__) {
        return (globalThis as any).__VITE_APP_VERSION__ as string;
    }
    if (typeof process !== 'undefined' && process.env?.VITE_APP_VERSION) {
        return process.env.VITE_APP_VERSION;
    }
    return undefined;
}

function getSessionId(): string | undefined {
    if (typeof window === 'undefined' || !window.localStorage) return undefined;
    const key = 'telemetry_session_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;

    const sid = globalThis?.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
    localStorage.setItem(key, sid);
    return sid;
}

function expiresAtDays(days: number, TimestampCtor: { fromDate: (date: Date) => unknown }) {
    const ms = Date.now() + days * 24 * 60 * 60 * 1000;
    return TimestampCtor.fromDate(new Date(ms));
}

function safePath(urlOrPath?: string) {
    if (!urlOrPath) return undefined;
    try {
        const u = new URL(urlOrPath);
        return u.pathname;
    } catch {
        return urlOrPath.split('?')[0];
    }
}

function pruneUndefined<T>(value: T): T {
    if (Array.isArray(value)) {
        return value.map((item) => pruneUndefined(item)).filter((item) => item !== undefined) as T;
    }
    if (value && typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>)
            .map(([key, val]) => [key, pruneUndefined(val)] as const)
            .filter(([, val]) => val !== undefined);
        return Object.fromEntries(entries) as T;
    }
    return value;
}

export async function logTelemetry(uid: string | undefined | null, e: TelemetryEvent) {
    const auth = getAuth();
    if (!uid || !auth.currentUser || uid !== auth.currentUser.uid) return;

    const { firestore, db } = await loadFirestoreDeps();
    const event: TelemetryEvent = pruneUndefined({
        ...e,
        requestPath: safePath(e.requestPath),
        sessionId: e.sessionId ?? getSessionId(),
        appVersion: e.appVersion ?? getAppVersion(),
        createdAt: firestore.serverTimestamp(),
        expiresAt: expiresAtDays(TTL_DAYS, firestore.Timestamp),
    });

    if (event.meta) {
        event.meta = pruneUndefined(event.meta);
        const json = JSON.stringify(event.meta);
        if (json.length > 6000) event.meta = { note: 'meta_too_large' };
    }

    await firestore.addDoc(firestore.collection(db, 'users', uid, 'telemetry'), event);
}
